/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import FlightDropzone from './FlightDropzone';
import styled from 'styled-components';
import { clearFlightInfoFields, clearTakeoffTimeError } from '../../../store/flightLegs/actions';
import {
  selectDeleteLegConfirmationVisible,
  selectIsFlightInfoLoading,
  selectFlightInfoError,
  selectUserId,
  selectSavingMissionPlan,
  selectUpdateTakeoffTimeError,
  selectIsUpdatingTakeoffTime,
  selectMissionPlanById,
  selectSelectedMissionPlanId,
  selectLoadingMissionPlanById,
  selectFailedToParseFlightLegs,
} from '../../../store';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import FlightLegsTable from './FlightLegsTable';
import DeleteLegModal from './DeleteLegModal';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import ErrorMessage from '../../common/layout/ErrorMessage';
import FlightInfoButtons from './FlightInfoButtons';
import { Link } from 'react-router-dom';
import { IconButton } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { isNil } from 'lodash';
import { getMissionPlanById } from '../../../store/missionPlans/actions';
import FailedToParseLegsAccordion from './FailedToParseLegsAccordion';

export interface FlightInfoProps {}

const StyledTitle = styled.div`
  color: ${appTheme.primaryTextColor};
  font-size: 24px;
  font-weight: 800;
  line-height: 32px;
  font-family: ${appTheme.viasatFontFamilyRegular};
  letter-spacing: 0.2px;
  margin-top: 88px;
  font-weight: 800;
`;
const StyledDisclaimer = styled.div`
  color: ${appTheme.primaryTextColor};
  font-family: ${appTheme.appFontFamily};
  margin-top: 5px;
  margin-bottom: 24px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const StyledPar = styled.p`
  margin: 0px;
`;

const StyledLink = styled(Link)`
  color: inherit;
  font-size: inherit;
`;

const FlightInfo = (props: FlightInfoProps) => {
  const dispatch = useDispatch();
  const showDeleteModal = useSelector(selectDeleteLegConfirmationVisible, shallowEqual);
  const isLoading = useSelector(selectIsFlightInfoLoading, shallowEqual);
  const isLoadingMission = useSelector(selectLoadingMissionPlanById, shallowEqual);
  const errorOccurred = useSelector(selectFlightInfoError, shallowEqual);
  const savingMissionPlan = useSelector(selectSavingMissionPlan, shallowEqual);
  const savedMissionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const missionPlanId = useSelector(selectSelectedMissionPlanId, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const isUpdatingTakeoffTime = useSelector(selectIsUpdatingTakeoffTime, shallowEqual);
  const updateTakeoffTimeError = useSelector(selectUpdateTakeoffTimeError, shallowEqual);
  const failedToParseFlightLegs = useSelector(selectFailedToParseFlightLegs, shallowEqual);

  useEffect(() => {
    if (missionPlanId !== undefined && savedMissionPlan.id !== missionPlanId && userId) {
      dispatch(getMissionPlanById(userId, missionPlanId));
    }
  }, [missionPlanId, userId, savedMissionPlan, dispatch, isUpdatingTakeoffTime]);

  // Clear flight info fields on unmount
  useEffect(() => {
    return () => {
      dispatch(clearFlightInfoFields());
      dispatch(clearTakeoffTimeError());
    };
  }, [dispatch]);

  return (
    <React.Fragment>
      {errorOccurred ? (
        <ErrorMessage>
          <StyledPar>
            An error occurred while updating flight leg. Please go back to the{' '}
            <StyledLink to="/plans">Mission Plans</StyledLink> page.
          </StyledPar>
        </ErrorMessage>
      ) : null}
      {!isNil(updateTakeoffTimeError) ? (
        <ErrorMessage>
          <StyledPar>An error occurred while setting the takeoff time.</StyledPar>

          <IconButton
            sx={{ marginLeft: 'auto', color: '#000000' }}
            onClick={() => {
              dispatch(clearTakeoffTimeError());
            }}
          >
            <CloseRoundedIcon color="inherit" />
          </IconButton>
        </ErrorMessage>
      ) : null}
      {!isLoading && !isLoadingMission && !savingMissionPlan && (!savedMissionPlan.id || !userId) ? (
        <ErrorMessage>
          <StyledPar>
            An error occurred while loading flight information. Please go back to the{' '}
            <StyledLink to="/plans">Mission Plans</StyledLink> page.
          </StyledPar>
        </ErrorMessage>
      ) : null}
      <StyledTitle>Input Flight Information</StyledTitle>
      <StyledDisclaimer>You may upload multiple files at once.</StyledDisclaimer>
      <FlightDropzone />
      {failedToParseFlightLegs?.length > 0 && (
        <FailedToParseLegsAccordion failedToParseFlightLegs={failedToParseFlightLegs} />
      )}
      <StyledTitle>Generate Legs</StyledTitle>
      <StyledDisclaimer>Reorder the imported flight legs as needed.</StyledDisclaimer>
      <FlightLegsTable />
      <FlightInfoButtons />
      {showDeleteModal ? <DeleteLegModal /> : null}
      {isLoading ? <ModalLoadingSpinner /> : null}
      {isUpdatingTakeoffTime ? <ModalLoadingSpinner /> : null}
    </React.Fragment>
  );
};

export default FlightInfo;
